import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../lib/useApi';
import { Spinner, ErrorBanner, Card, SectionTitle } from '../../components/ui';
import { api, ApiError } from '../../lib/api';

interface League {
  id: string;
  name: string;
  slug: string;
  active: boolean;
}

export function AdminLeaguesPage() {
  const { data, error, loading, reload } = useApi<League[]>('/admin/leagues');
  const [name, setName] = useState('');
  const [formErr, setFormErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setFormErr('');
    setBusy(true);
    try {
      await api.post('/admin/leagues', { name });
      setName('');
      reload();
    } catch (err) {
      setFormErr(err instanceof ApiError ? err.message : 'Unable to create league.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;

  return (
    <div className="space-y-4">
      <SectionTitle>Leagues</SectionTitle>

      <Card>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-2 py-2">Name</th>
              <th className="px-2 py-2">Slug</th>
              <th className="px-2 py-2">Status</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data?.map((l) => (
              <LeagueRow key={l.id} league={l} onChanged={reload} />
            ))}
            {data?.length === 0 && (
              <tr>
                <td colSpan={4} className="px-2 py-4 text-center text-muted">
                  No leagues yet. Create one below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card>
        <h3 className="mb-3 font-display text-xl uppercase text-ink">New league</h3>
        <form onSubmit={create} className="flex flex-wrap items-end gap-3">
          <label className="text-xs">
            <span className="mb-1 block text-muted">Name</span>
            <input
              type="text"
              className="input w-64"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="btn-primary" disabled={busy}>
            Create
          </button>
          {formErr && <span className="text-sm text-danger">{formErr}</span>}
        </form>
      </Card>
    </div>
  );
}

function LeagueRow({ league, onChanged }: { league: League; onChanged: () => void }) {
  const [name, setName] = useState(league.name);
  const [busy, setBusy] = useState(false);

  async function rename() {
    if (name === league.name) return;
    setBusy(true);
    try {
      await api.patch(`/admin/leagues/${league.id}`, { name });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive() {
    setBusy(true);
    try {
      await api.patch(`/admin/leagues/${league.id}`, { active: !league.active });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-2 py-2">
        <input
          type="text"
          className="input w-48"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </td>
      <td className="px-2 py-2 text-muted">{league.slug}</td>
      <td className="px-2 py-2">
        {league.active ? (
          <span className="chip bg-success/15 text-success">Active</span>
        ) : (
          <span className="chip bg-danger/10 text-danger">Inactive</span>
        )}
      </td>
      <td className="px-2 py-2 text-right">
        <div className="flex justify-end gap-2">
          <Link to="/admin/staff" className="text-grove underline">
            Staff
          </Link>
          <button className="btn-outline" onClick={rename} disabled={busy || name === league.name}>
            Save
          </button>
          <button
            className={league.active ? 'text-danger underline' : 'text-success underline'}
            onClick={toggleActive}
            disabled={busy}
          >
            {league.active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </td>
    </tr>
  );
}
